// /opt/isp_erp/frontend/src/components/tickets/CalendarioPermisos.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from '../../api/axios';

const locales = { 'es': es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const estadoColores = {
  pendiente: '#ff9800',
  aprobado: '#4caf50',
  rechazado: '#f44336',
  completado: '#9e9e9e'
};

const estadoLabels = {
  pendiente: '⏳ Pendiente',
  aprobado: '✅ Aprobado',
  rechazado: '❌ Rechazado',
  completado: '📌 Completado'
};

const EventComponent = ({ event }) => {
  return (
    <Tooltip title={`${event.tecnico} - ${event.motivo} (${estadoLabels[event.estado] || event.estado})`} arrow>
      <div style={{ 
        backgroundColor: event.color,
        borderRadius: '4px',
        color: 'white',
        padding: '2px 6px',
        fontSize: '11px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        height: '100%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center'
      }}>
        <strong>{event.tecnico}</strong>: {event.motivo}
      </div>
    </Tooltip>
  );
};

export default function CalendarioPermisos({ open, onClose }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      cargarPermisos();
    }
  }, [open]);

  const cargarPermisos = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/tickets/tecnicos/permisos/todos');
      const data = response.data || [];

      console.log('📋 Todos los permisos:', data);

      const eventosFormateados = data.map(p => {
        // ✅ Crear fechas SIN zona horaria (local)
        const [yearInicio, monthInicio, dayInicio] = p.fecha_inicio.split('-').map(Number);
        const [yearFin, monthFin, dayFin] = p.fecha_fin.split('-').map(Number);
        
        const start = new Date(yearInicio, monthInicio - 1, dayInicio);
        const end = new Date(yearFin, monthFin - 1, dayFin);

        // Si tiene horas, ajustar (sin zona horaria)
        if (p.hora_inicio) {
          const [h, m] = p.hora_inicio.split(':').map(Number);
          start.setHours(h, m, 0, 0);
        }
        if (p.hora_fin) {
          const [h, m] = p.hora_fin.split(':').map(Number);
          end.setHours(h, m, 0, 0);
        }

        const allDay = !p.hora_inicio && !p.hora_fin;
        
        // ✅ Para día completo, sumar 1 día al end
        if (allDay) {
          end.setDate(end.getDate() + 1);
        }

        const tecnicoNombre = p.tecnico_nombre || `Técnico #${p.tecnico_id}`;
        const motivo = p.motivo || 'Sin motivo';

        console.log(`📅 Evento: ${tecnicoNombre} - ${start.toLocaleString()} → ${end.toLocaleString()}`);

        return {
          id: p.id,
          title: `${tecnicoNombre} - ${motivo}`,
          start: start,
          end: end,
          estado: p.estado,
          tecnico: tecnicoNombre,
          tecnico_id: p.tecnico_id,
          motivo: motivo,
          allDay: allDay,
          color: estadoColores[p.estado] || '#9e9e9e'
        };
      });

      console.log('📅 Eventos formateados:', eventosFormateados);
      setEventos(eventosFormateados);
    } catch (err) {
      console.error('❌ Error cargando permisos:', err);
      setError(err.response?.data?.detail || 'Error al cargar permisos');
    } finally {
      setLoading(false);
    }
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '4px',
      color: 'white',
      border: 'none',
      opacity: event.estado === 'rechazado' ? 0.5 : 1,
      padding: '2px 6px',
      fontSize: '11px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  });

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">📅 Calendario de Permisos</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="⏳ Pendiente" sx={{ bgcolor: '#ff9800', color: 'white' }} size="small" />
            <Chip label="✅ Aprobado" sx={{ bgcolor: '#4caf50', color: 'white' }} size="small" />
            <Chip label="❌ Rechazado" sx={{ bgcolor: '#f44336', color: 'white' }} size="small" />
            <Chip label="📌 Completado" sx={{ bgcolor: '#9e9e9e', color: 'white' }} size="small" />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, height: '70vh' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Calendar
              localizer={localizer}
              events={eventos}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              views={['month', 'week', 'day']}
              defaultView="month"
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              components={{
                event: EventComponent
              }}
              messages={{
                next: "Siguiente",
                previous: "Anterior",
                today: "Hoy",
                month: "Mes",
                week: "Semana",
                day: "Día"
              }}
              culture="es"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="outlined" onClick={cargarPermisos} disabled={loading}>
          🔄 Actualizar
        </Button>
      </DialogActions>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle del Permiso</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">
                <strong>Técnico:</strong> {selectedEvent.tecnico || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Fecha inicio:</strong> {selectedEvent.start?.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha fin:</strong> {selectedEvent.end?.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Motivo:</strong> {selectedEvent.motivo || 'Sin motivo'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Estado:</strong> {estadoLabels[selectedEvent.estado] || selectedEvent.estado}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
